import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  getPenaltyTypes,
  searchPenaltyCab,
  getPenaltyFollowups,
  createPenaltyFollowup,
  updatePenaltyFollowup,
  markPenaltySmsSent,
  markPenaltyPaid,
} from "../../services/penaltyFollowupService";

import "./PenaltyFollowUps.css";

const getToday = () => {
  return new Date()
    .toISOString()
    .split("T")[0];
};

const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  const parts = String(value).split("-");

  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }

  return value;
};

const getSmsReason = (penaltyTypeName) => {
  const type = String(
    penaltyTypeName || "Penalty"
  )
    .trim()
    .toLowerCase();

  if (type.includes("absent")) {
    return "Absent";
  }

  if (type.includes("misuse")) {
    return "Misuse";
  }

  if (type.includes("outstation")) {
    return "Outstation";
  }

  if (
    type.includes("over speed") ||
    type.includes("overspeed")
  ) {
    return "Over Speed";
  }

  const fallback =
    String(
      penaltyTypeName || "Penalty"
    ).trim();

  return fallback.length > 18
    ? fallback.slice(0, 18).trim()
    : fallback;
};

const buildSmsReason = (record) => {
  const reason = getSmsReason(
    record?.penaltyTypeName
  );

  const date = record?.penaltyDate
    ? formatDate(record.penaltyDate)
    : "";

  return date
    ? `${reason} on ${date}`
    : reason;
};

const getAssignedPerson = (record) => {
  return (
    record?.driver ||
    record?.driverName ||
    record?.supplier ||
    record?.supplierName ||
    "Not Assigned"
  );
};

const getAssignedContact = (record) => {
  return (
    record?.driverMobile ||
    record?.supplierMobile ||
    "Not Available"
  );
};

const getSmsPersonName = (record) => {
  return getAssignedPerson(record);
};

const PenaltyFollowUps = ({
  embedded = false,
}) => {
  const today = getToday();

  const [fromDate, setFromDate] =
    useState(today);

  const [toDate, setToDate] =
    useState(today);

  const [penaltyDate, setPenaltyDate] =
    useState(today);

  const [cabLast4, setCabLast4] =
    useState("");

  const [penaltyTypeId, setPenaltyTypeId] =
    useState("");

  const [penaltyAmount, setPenaltyAmount] =
    useState("");

  const [penaltyRemarks, setPenaltyRemarks] =
    useState("");

  const [statusFilter, setStatusFilter] =
    useState("");

  const [penaltyTypes, setPenaltyTypes] =
    useState([]);

  const [searchResult, setSearchResult] =
    useState(null);

  const [records, setRecords] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [searching, setSearching] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  const [success, setSuccess] =
    useState("");

  const [editingId, setEditingId] =
    useState(null);

  const [editRemarks, setEditRemarks] =
    useState("");

  const [editAmount, setEditAmount] =
    useState("");

  const [editStatus, setEditStatus] =
    useState("Pending");

  const [smsRecord, setSmsRecord] =
    useState(null);

  const [smsSending, setSmsSending] =
    useState(false);

  const loadPenaltyTypes =
    useCallback(async () => {
      try {
        const data =
          await getPenaltyTypes();

        setPenaltyTypes(
          Array.isArray(data)
            ? data
            : []
        );
      } catch (err) {
        setError(
          err.message ||
            "Unable to load penalty types."
        );
      }
    }, []);

  const loadRecords =
    useCallback(async () => {
      try {
        setLoading(true);
        setError("");

        const data =
          await getPenaltyFollowups({
            fromDate,
            toDate,
            penaltyTypeId,
            status: statusFilter,
          });

        setRecords(
          Array.isArray(data)
            ? data
            : []
        );
      } catch (err) {
        setError(
          err.message ||
            "Unable to load penalty follow-ups."
        );
      } finally {
        setLoading(false);
      }
    }, [
      fromDate,
      toDate,
      penaltyTypeId,
      statusFilter,
    ]);

  useEffect(() => {
    loadPenaltyTypes();
  }, [loadPenaltyTypes]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleSearch = async () => {
    if (searching) {
      return;
    }

    setError("");
    setSuccess("");
    setSearchResult(null);

    if (!/^\d{4}$/.test(cabLast4)) {
      setError(
        "Enter exactly 4 digits of the cab number."
      );
      return;
    }

    if (!penaltyDate) {
      setError(
        "Please select penalty date."
      );
      return;
    }

    try {
      setSearching(true);

      const result =
        await searchPenaltyCab({
          cabLast4,
          penaltyDate,
        });

      setSearchResult(result);

      if (result?.penaltyTypeId) {
        setPenaltyTypeId(
          String(result.penaltyTypeId)
        );
      } else {
        setPenaltyTypeId("");
      }
    } catch (err) {
      setError(
        err.message ||
          "Cab not found."
      );
    } finally {
      setSearching(false);
    }
  };

  const handleCreate = async () => {
    if (saving) {
      return;
    }

    setError("");
    setSuccess("");

    if (!searchResult) {
      setError(
        "Search the cab before creating the penalty."
      );
      return;
    }

    const resolvedPenaltyTypeId =
      penaltyTypeId ||
      searchResult.penaltyTypeId;

    if (!resolvedPenaltyTypeId) {
      setError(
        "Penalty type was not found."
      );
      return;
    }

    if (!String(penaltyAmount).trim()) {
      setError("Please enter the penalty amount.");
      return;
    }

    const parsedPenaltyAmount = Number(penaltyAmount);

    if (
      !Number.isFinite(parsedPenaltyAmount) ||
      parsedPenaltyAmount < 0
    ) {
      setError("Please enter a valid penalty amount.");
      return;
    }

    try {
      setSaving(true);

      await createPenaltyFollowup({
        penaltyTypeId:
          resolvedPenaltyTypeId,

        penaltyDate:
          penaltyDate,

        vehicleId:
          searchResult.vehicleId,

        vehicleAssignmentId:
          searchResult.vehicleAssignmentId,

        supplierId:
          searchResult.supplierId,

        driverId:
          searchResult.driverId,

        penaltyAmount:
          parsedPenaltyAmount,

        penaltyRemarks:
          penaltyRemarks.trim(),
      });

      setSuccess(
        "Penalty added successfully."
      );

      setCabLast4("");
      setSearchResult(null);
      setPenaltyTypeId("");
      setPenaltyAmount("");
      setPenaltyRemarks("");

      await loadRecords();
    } catch (err) {
      setError(
        err.message ||
          "Unable to create penalty."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setCabLast4("");
    setSearchResult(null);
    setPenaltyTypeId("");
    setPenaltyAmount("");
    setPenaltyRemarks("");
    setError("");
    setSuccess("");
  };

  const handleEdit = (record) => {
    setEditingId(record.id);

    setEditRemarks(
      record.remarks || ""
    );

    setEditAmount(
      record.penaltyAmount ?? ""
    );

    setEditStatus(
      record.status || "Pending"
    );

    setError("");
    setSuccess("");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditRemarks("");
    setEditAmount("");
    setEditStatus("Pending");
  };

  const handleUpdate = async (
    record
  ) => {
    if (saving) {
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      await updatePenaltyFollowup(
        record.id,
        {
          penaltyTypeId:
            record.penaltyTypeId,

          penaltyDate:
            record.penaltyDate,

          penaltyAmount:
            editAmount === ""
              ? null
              : Number(editAmount),

          penaltyRemarks:
            editRemarks.trim(),

          status:
            editStatus,
        }
      );

      setSuccess(
        "Penalty updated successfully."
      );

      handleCancelEdit();

      await loadRecords();
    } catch (err) {
      setError(
        err.message ||
          "Unable to update penalty."
      );
    } finally {
      setSaving(false);
    }
  };

  const openSmsPopup = (
    record
  ) => {
    if (record.smsSent) {
      return;
    }

    setSmsRecord(record);

    setError("");
    setSuccess("");
  };

  const closeSmsPopup = () => {
    if (smsSending) {
      return;
    }

    setSmsRecord(null);
  };

  const handleSms = async () => {
    if (!smsRecord || smsSending) {
      return;
    }

    try {
      setSmsSending(true);
      setError("");
      setSuccess("");

      const updatedRecord =
        await markPenaltySmsSent(
          smsRecord.id
        );

      if (!updatedRecord) {
        throw new Error(
          "SMS sending failed."
        );
      }

      setSuccess(
        "SMS sent successfully."
      );

      setSmsRecord(null);

      await loadRecords();
    } catch (err) {
      setError(
        err.message ||
          "Unable to send SMS."
      );
    } finally {
      setSmsSending(false);
    }
  };

  const handlePaid = async (
    record
  ) => {
    if (record.status === "Paid") {
      return;
    }

    const confirmed =
      window.confirm(
        `Mark penalty for ${
          record.cabNumber ||
          "this cab"
        } as paid?`
      );

    if (!confirmed) {
      return;
    }

    try {
      setError("");
      setSuccess("");

      await markPenaltyPaid(
        record.id
      );

      setSuccess(
        "Penalty marked as paid."
      );

      await loadRecords();
    } catch (err) {
      setError(
        err.message ||
          "Unable to mark penalty as paid."
      );
    }
  };

  return (
    <div
      className={`penalty-followups-page ${
        embedded
          ? "penalty-followups-embedded"
          : ""
      }`}
    >
      {error && (
        <div className="penalty-message penalty-error">
          {error}
        </div>
      )}

      {success && (
        <div className="penalty-message penalty-success">
          {success}
        </div>
      )}

      <section className="penalty-card penalty-search-card">
        <h2>
          Add Penalty Follow-up
        </h2>

        <div className="penalty-search-grid">
          <div className="penalty-field">
            <label>
              Date <span>*</span>
            </label>

            <input
              type="date"
              value={penaltyDate}
              onChange={(e) =>
                setPenaltyDate(
                  e.target.value
                )
              }
            />
          </div>

          <div className="penalty-field">
            <label>
              Cab Last 4 Digits{" "}
              <span>*</span>
            </label>

            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              value={cabLast4}
              placeholder="Enter last 4 digits"
              onChange={(e) =>
                setCabLast4(
                  e.target.value
                    .replace(/\D/g, "")
                    .slice(0, 4)
                )
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch();
                }
              }}
            />
          </div>

          <button
            type="button"
            className="penalty-primary-btn penalty-search-btn"
            onClick={handleSearch}
            disabled={searching}
          >
            {searching
              ? "Searching..."
              : "Search Cab"}
          </button>
        </div>
      </section>

      {searchResult && (
        <section className="penalty-card penalty-details-card">
          <div className="penalty-details-top">
            <h2>Penalty Details</h2>

            <div className="penalty-header-buttons">
              <button
                type="button"
                className="penalty-secondary-btn"
                onClick={handleClear}
              >
                Clear
              </button>

              <button
                type="button"
                className="penalty-primary-btn"
                onClick={handleCreate}
                disabled={saving}
              >
                {saving
                  ? "Adding..."
                  : "Add Penalty"}
              </button>
            </div>
          </div>

          <div className="penalty-summary">
            <div>
              <span>Cab Number</span>
              <strong>
                {searchResult.vehicleRegistrationNumber ||
                  searchResult.cabNumber ||
                  "—"}
              </strong>
            </div>

            <div>
              <span>Driver / Supplier</span>
              <strong>
                {getAssignedPerson(searchResult)}
              </strong>
            </div>

            <div>
              <span>Contact Number</span>
              <strong>
                {getAssignedContact(searchResult)}
              </strong>
            </div>

            <div>
              <span>Vehicle Type</span>
              <strong>
                {searchResult.vehicleType ||
                  "—"}
              </strong>
            </div>

            <div>
              <span>Year</span>
              <strong>
                {searchResult.year || "—"}
              </strong>
            </div>
          </div>

          <div className="penalty-after-search-row">
            <div className="penalty-field penalty-type-display">
              <label>
                Penalty Type <span>*</span>
              </label>

              <select
                className="penalty-details-select"
                value={
                  penaltyTypeId || ""
                }
                onChange={(e) => {
                  const selectedId =
                    e.target.value;

                  setPenaltyTypeId(
                    selectedId
                  );

                  const selectedType =
                    penaltyTypes.find(
                      (type) =>
                        String(type.id) ===
                        String(selectedId)
                    );

                  setSearchResult(
                    (prev) =>
                      prev
                        ? {
                            ...prev,
                            penaltyTypeId:
                              selectedId
                                ? Number(
                                    selectedId
                                  )
                                : null,
                            penaltyTypeName:
                              selectedType?.penaltyName ||
                              "",
                          }
                        : prev
                  );
                }}
              >
                <option value="">
                  Select Penalty Type
                </option>

                {penaltyTypes.map(
                  (type) => (
                    <option
                      key={type.id}
                      value={type.id}
                    >
                      {type.penaltyName}
                    </option>
                  )
                )}
              </select>
            </div>

            <div className="penalty-status-display">
              <label>Status</label>

              <div className="penalty-status-box penalty-detail-status">
                <strong>Pending</strong>
              </div>
            </div>

            <div className="penalty-field">
              <label>
                Penalty Amount <span>*</span>
              </label>

              <input
                type="number"
                min="0"
                step="0.01"
                value={penaltyAmount}
                placeholder="Enter penalty amount"
                onChange={(e) =>
                  setPenaltyAmount(e.target.value)
                }
              />
            </div>

            <div className="penalty-field penalty-remarks-field">
              <label>Remarks (Optional)</label>

              <input
                type="text"
                maxLength={500}
                value={penaltyRemarks}
                placeholder="Enter remarks"
                onChange={(e) =>
                  setPenaltyRemarks(e.target.value)
                }
              />
            </div>
          </div>
        </section>
      )}

      <section className="penalty-card penalty-list-card">
        <div className="penalty-list-heading">
          <div>
            <h2>
              Penalty Follow-ups List
            </h2>

            <p>
              {records.length}{" "}
              {records.length === 1
                ? "entry"
                : "entries"}
            </p>
          </div>

          {/* <button
            type="button"
            className="penalty-secondary-btn"
            onClick={loadRecords}
            disabled={loading}
          >
            ↻ Refresh
          </button> */}
        </div>

        <div className="penalty-filter-row">
          <div className="penalty-field">
            <label>From Date</label>

            <input
              type="date"
              value={fromDate}
              onChange={(e) =>
                setFromDate(
                  e.target.value
                )
              }
            />
          </div>

          <div className="penalty-field">
            <label>To Date</label>

            <input
              type="date"
              value={toDate}
              onChange={(e) =>
                setToDate(
                  e.target.value
                )
              }
            />
          </div>

          <div className="penalty-field">
            <label>Penalty Type</label>

            <select
              value={penaltyTypeId}
              onChange={(e) =>
                setPenaltyTypeId(
                  e.target.value
                )
              }
            >
              <option value="">
                All Penalty Types
              </option>

              {penaltyTypes.map(
                (type) => (
                  <option
                    key={type.id}
                    value={type.id}
                  >
                    {type.penaltyName}
                  </option>
                )
              )}
            </select>
          </div>

          <div className="penalty-field">
            <label>Status</label>

            <select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(
                  e.target.value
                )
              }
            >
              <option value="">
                All Status
              </option>

              <option value="Pending">
                Pending
              </option>

              <option value="Paid">
                Paid
              </option>

              <option value="Cancelled">
                Cancelled
              </option>
            </select>
          </div>

          <button
            type="button"
            className="penalty-primary-btn filter-button"
            onClick={loadRecords}
            disabled={loading}
          >
            Filter
          </button>
        </div>

        <div className="penalty-table-container">
          <table className="penalty-table">
            <thead>
              <tr>
                <th>S.NO</th>
                <th>DATE</th>
                <th>CAB NUMBER</th>
                <th>PENALTY TYPE</th>
                <th>AMOUNT</th>
                <th>SUPPLIER / DRIVER</th>
                <th>CONTACT NUMBER</th>
                <th>VEHICLE TYPE</th>
                <th>YEAR</th>
                <th>REMARKS</th>
                <th>SMS</th>
                <th>STATUS</th>
                <th>UPDATED BY</th>
                <th>ACTIONS</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="14"
                    className="penalty-empty"
                  >
                    Loading...
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td
                    colSpan="14"
                    className="penalty-empty"
                  >
                    No penalty follow-ups found.
                  </td>
                </tr>
              ) : (
                records.map(
                  (record, index) => (
                    <tr
                      key={record.id}
                    >
                      <td>
                        {index + 1}
                      </td>

                      <td>
                        {formatDate(
                          record.penaltyDate
                        )}
                      </td>

                      <td>
                        <strong className="cab-number">
                          {record.cabNumber ||
                            record.vehicleRegistrationNumber ||
                            "—"}
                        </strong>
                      </td>

                      <td>
                        {record.penaltyTypeName ||
                          "—"}
                      </td>

                      <td>
                      {editingId === record.id ? (
                        <input
                          className="penalty-edit-input"
                          type="number"
                          min="0"
                          step="0.01"
                          value={editAmount}
                          onChange={(e) =>
                            setEditAmount(e.target.value)
                          }
                        />
                      ) : (
                        <>₹ {record.penaltyAmount ?? "0"}</>
                      )}
                    </td>

                      <td>
                        {getAssignedPerson(record)}
                      </td>

                      <td>
                        {getAssignedContact(record)}
                      </td>

                      <td>
                        {record.vehicleType ||
                          "—"}
                      </td>

                      <td>
                        {record.year ||
                          "—"}
                      </td>

                      <td className="remarks-cell">
                        {editingId ===
                        record.id ? (
                          <input
                            className="penalty-edit-input"
                            value={
                              editRemarks
                            }
                            maxLength={500}
                            onChange={(e) =>
                              setEditRemarks(
                                e.target.value
                              )
                            }
                          />
                        ) : (
                          record.remarks ||
                          "—"
                        )}
                      </td>

                      <td>
                        {record.smsSent ? (
                          <span className="penalty-badge sms-sent">
                            Sent
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="penalty-small-btn sms-button"
                            onClick={() =>
                              openSmsPopup(
                                record
                              )
                            }
                          >
                            Send SMS
                          </button>
                        )}
                      </td>

                      <td>
                        {editingId ===
                        record.id ? (
                          <select
                            className="penalty-edit-select"
                            value={
                              editStatus
                            }
                            onChange={(e) =>
                              setEditStatus(
                                e.target.value
                              )
                            }
                          >
                            <option value="Pending">
                              Pending
                            </option>

                            <option value="Paid">
                              Paid
                            </option>

                            <option value="Cancelled">
                              Cancelled
                            </option>
                          </select>
                        ) : (
                          <span
                            className={`penalty-badge status-${String(
                              record.status ||
                                "Pending"
                            ).toLowerCase()}`}
                          >
                            {record.status ||
                              "Pending"}
                          </span>
                        )}
                      </td>

                      <td>
                        {record.updatedBy ||
                          "—"}
                      </td>

                      <td>
                        <div className="penalty-actions">
                          {editingId ===
                          record.id ? (
                            <>
                              <button
                                type="button"
                                className="penalty-small-btn"
                                onClick={() =>
                                  handleUpdate(
                                    record
                                  )
                                }
                                disabled={
                                  saving
                                }
                              >
                                {saving
                                  ? "Saving..."
                                  : "Save"}
                              </button>

                              <button
                                type="button"
                                className="penalty-small-btn secondary"
                                onClick={
                                  handleCancelEdit
                                }
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="penalty-small-btn"
                                onClick={() =>
                                  handleEdit(
                                    record
                                  )
                                }
                              >
                                Edit
                              </button>

                              {record.status !==
                                "Paid" && (
                                <button
                                  type="button"
                                  className="penalty-small-btn paid"
                                  onClick={() =>
                                    handlePaid(
                                      record
                                    )
                                  }
                                >
                                  Mark Paid
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                )
              )}
            </tbody>
          </table>
        </div>
      </section>

      {smsRecord && (
        <div
          className="penalty-modal-overlay"
          onClick={closeSmsPopup}
        >
          <div
            className="penalty-sms-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >
            <div className="penalty-modal-header">
              <div>
                <h3>
                  Send Penalty SMS
                </h3>

                <p>
                  Review the SMS before sending.
                  The message is generated from
                  the saved penalty details.
                </p>
              </div>

              <button
                type="button"
                className="penalty-modal-close"
                onClick={
                  closeSmsPopup
                }
                disabled={
                  smsSending
                }
              >
                ×
              </button>
            </div>

            <div className="penalty-modal-details">
              <div>
                <span>
                  Cab Number
                </span>

                <strong>
                  {smsRecord.cabNumber ||
                    smsRecord.vehicleRegistrationNumber ||
                    "—"}
                </strong>
              </div>

              <div>
                <span>
                  Penalty Type
                </span>

                <strong>
                  {smsRecord.penaltyTypeName ||
                    "—"}
                </strong>
              </div>

              <div>
                <span>
                  Contact Number
                </span>

                <strong>
                  {getAssignedContact(smsRecord)}
                </strong>
              </div>
            </div>

            <div className="penalty-sms-modal-preview">
              <h4>
                SMS Preview
              </h4>

              <p>
                Dear{" "}
                <strong>
                  {getSmsPersonName(smsRecord)}
                </strong>
                , a penalty of Rs.
                <strong>
                  {smsRecord.penaltyAmount ?? "0"}
                </strong>
                {" "}has been applied for vehicle{" "}
                <strong>
                  {smsRecord.cabNumber ||
                    smsRecord.vehicleRegistrationNumber ||
                    "—"}
                </strong>
                {" "}due to{" "}
                <strong>
                  {buildSmsReason(smsRecord)}
                </strong>
                . Please clear the penalty amount
                at the earliest. Srinivasa Tours
                and Travels.
              </p>
            </div>

            <div className="penalty-modal-actions">
              <button
                type="button"
                className="penalty-secondary-btn"
                onClick={
                  closeSmsPopup
                }
                disabled={
                  smsSending
                }
              >
                Cancel
              </button>

              <button
                type="button"
                className="penalty-primary-btn"
                onClick={handleSms}
                disabled={smsSending}
              >
                {smsSending
                  ? "Sending..."
                  : "Send SMS"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PenaltyFollowUps;